import { hasLoss } from "./rows";

/**
 * The URL folders of the pages with an issue, as a tree. A page belongs to the folder its path ends
 * in, query string dropped: `/modelli/tonale/specs` and `/modelli/tonale/` both to `/modelli/tonale/`;
 * pages at the root (`/`, `/contatti`) to `/`, which has no subfolders.
 */
const ROOT = "/";

const OWN_PAGES = "Pages in this folder";

const folderOf = (uri) => {
    const { pathname } = new URL(uri);
    return pathname.slice(0, pathname.lastIndexOf("/") + 1);
};

/** `/modelli/tonale/` → [`/modelli/`, `/modelli/tonale/`]; `/` → [`/`]. */
export const foldersAbove = (folder) => {
    const segments = folder.split("/").filter(Boolean);
    if (segments.length === 0) return [ROOT];
    return segments.map((_, index) => `/${segments.slice(0, index + 1).join("/")}/`);
};

export const isFolder = (path) => typeof path === "string" && path.startsWith("/") && path.endsWith("/");

export const isUnder = (folder) => (uri) => (folder === ROOT ? folderOf(uri) === ROOT : folderOf(uri).startsWith(folder));

/**
 * One node per folder holding rows, each with the rows under it, what `lossOf` reads of them (`impact` and
 * `lostPerMonth`) and its subfolders, sorted by impact, largest first; folders with no impact after those with one, by rows.
 * A folder with subfolders whose own pages hold rows too lists those rows among its subfolders as one
 * `ownPages` node, which has the folder's path, so its subfolders and that node add up to the folder.
 */
export const folderTree = (rows, lossOf) => {
    const rowsByFolder = new Map();
    for (const row of rows) {
        for (const folder of foldersAbove(folderOf(row.page.uri))) {
            if (!rowsByFolder.has(folder)) rowsByFolder.set(folder, []);
            rowsByFolder.get(folder).push(row);
        }
    }
    const subfolders = new Map();
    for (const folder of rowsByFolder.keys()) {
        const parent = foldersAbove(folder).slice(-2, -1)[0] ?? null;
        subfolders.set(parent, [...(subfolders.get(parent) || []), folder]);
    }
    const ownPagesOf = (folder) => {
        const own = rowsByFolder.get(folder).filter((row) => folderOf(row.page.uri) === folder);
        if (own.length === 0 || !subfolders.has(folder)) return [];
        return [{ path: folder, name: OWN_PAGES, ownPages: true, rows: own.length, ...lossOf(own), children: [] }];
    };
    const childrenOf = (parent) => [
        ...(subfolders.get(parent) || []).map((folder) => ({
            path: folder,
            name: parent === null ? folder : folder.slice(parent.length),
            rows: rowsByFolder.get(folder).length,
            ...lossOf(rowsByFolder.get(folder)),
            children: childrenOf(folder),
        })),
        ...(parent === null ? [] : ownPagesOf(parent)),
    ].sort((a, b) => b.impact - a.impact || b.rows - a.rows || a.path.localeCompare(b.path));
    return childrenOf(null);
};

/** The tree without its folders and `ownPages` nodes that have no loss on any measure, at any depth; an `ownPages` node goes with the last subfolder beside it. */
export const withLoss = (nodes) => {
    const kept = nodes
        .filter(hasLoss)
        .map((node) => ({ ...node, children: withLoss(node.children) }));
    return kept.some((node) => !node.ownPages) ? kept : [];
};
